/*
 * 犬｜数量をかけた合計
 *
 * 商品ページの価格は単価なので、数量を増やしても変わらない。
 * 「合計 5,500円」を数量セレクタのとなりに出して、カートに入れる前に合計が分かるようにする。
 *
 * 金額は表示中の価格から読む。バリアントや定期プランを切り替えたときに
 * 自分で計算し直すと、表示と食い違う余地が生まれる。
 */

class InuQuantityTotal extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {MutationObserver | null} */
  #priceObserver = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#form = this.#scope?.querySelector('form[data-type="add-to-cart-form"]') ?? null;

    this.#form?.addEventListener('input', this.#handleChange);
    this.#form?.addEventListener('change', this.#handleChange);
    this.#form?.addEventListener('click', this.#handleClick);

    // バリアントを切り替えると価格の要素ごと差し替わる。
    // 要素そのものを見ていると監視が切れるので、購入エリアの変化を見る
    if (this.#scope) {
      this.#priceObserver = new MutationObserver(() => this.#render());
      this.#priceObserver.observe(this.#scope, { childList: true, subtree: true, characterData: true });
    }

    this.#render();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('input', this.#handleChange);
    this.#form?.removeEventListener('change', this.#handleChange);
    this.#form?.removeEventListener('click', this.#handleClick);
    this.#priceObserver?.disconnect();
  }

  #handleChange = () => this.#render();

  /** 数量の ± はクリックの時点ではまだ古い値なので、書き換わったあとに読み直す */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('quantity-selector-component')) return;
    requestAnimationFrame(() => this.#render());
  };

  /** 価格を出している要素。合計はここの表示から組み立てる */
  #priceElement() {
    return this.#scope?.querySelector('product-price [ref="priceContainer"]') ?? null;
  }

  /** いま選ばれている数量。入力が無ければ1 */
  #quantity() {
    const input = this.#form?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;
    return Math.max(Number(input.value) || 1, 1);
  }

  /** 表示中の単価（円）。読めなければ null */
  #unitPrice() {
    const shown = this.#priceElement()?.querySelector('.price')?.textContent;
    const parsed = Number(String(shown ?? '').replace(/[^0-9]/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  #render() {
    const quantity = this.#quantity();
    const unitPrice = this.#unitPrice();

    if (unitPrice == null) {
      this.#write('');
      return;
    }

    const total = unitPrice * quantity;
    this.#write(`合計 ${total.toLocaleString('ja-JP')}円`);
  }

  /**
   * 変わったときだけ書き込む。毎回書くと、自分の書き換えで監視が反応し続ける
   * @param {string} text
   */
  #write(text) {
    if (this.textContent === text) return;
    this.textContent = text;
    this.hidden = text === '';
  }
}

if (!customElements.get('inu-quantity-total')) {
  customElements.define('inu-quantity-total', InuQuantityTotal);
}
